// REACT IMPORTS
import { useNavigate, useParams } from "react-router-dom";

// COMPONENT IMPORTS
import { useAppDispatch } from "store/hooks";
import {
  notificationCountGet,
  updateTokenExpiredStatus,
  updateTokenStatus,
  useAccountDetails,
  userGoogleLogin,
} from "store/slices/authSlice";
import { onSetIsLogin } from "store/slices/commonSlice";
import { TOKEN_STORAGE_KEY, TOKEN_STORAGE_TIME } from "store/api/axiosApi";
import { decode } from "utils/HelperFunctions";
import { SettingsValue } from "types/payments";

interface UseGoogleAuthOptions {
  guestLogin?: boolean;
  setShowOverlay?: (show: boolean) => void;
  // Guest login on a link page, on a cluster with two-factor login: the
  // overlay has to ask for the one-time code before it may close.
  onOtpRequired?: () => void;
  onStart?: () => void;
  onFinish?: () => void;
  onNoMatch?: () => void;
  // When the verified Google email is attached to multiple accounts. The
  // opaque attempt ID preserves the verified server-side context for stage 2.
  onMultiple?: (challenge: GoogleLoginChallenge) => void;
}

export interface GoogleLoginChallenge {
  email: string;
  googleLoginAttemptId: string;
}

interface GoogleAccountSelection {
  googleLoginAttemptId: string;
  accountId: string;
}

/* ------------------------------------- GOOGLE AUTH HOOK ------------------------------------- */

// Shared "Sign in with Google" behavior for the login, login-via-cred and
// sign-up pages, mirroring the regular login's post-success flow.
export const useGoogleAuth = ({
  guestLogin,
  setShowOverlay,
  onOtpRequired,
  onStart,
  onFinish,
  onNoMatch,
  onMultiple,
}: UseGoogleAuthOptions = {}) => {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const accountDetails = useAccountDetails();
  const { clusterId: cluster_id, id: actionId } = useParams();
  const clusterId = decode(cluster_id || "", true);

  const getClusterId = () =>
    clusterId
      ? clusterId
      : actionId
        ? decode(actionId || "").split("_")[1]
        : "";

  const completeLogin = (res: any) => {
    localStorage.setItem(TOKEN_STORAGE_KEY, res?.body?.token);
    localStorage.setItem(TOKEN_STORAGE_TIME, `${new Date().getTime()}`);
    dispatch(updateTokenStatus(false));
    dispatch(updateTokenExpiredStatus(false));

    // The server decides whether a one-time code is still needed: after a
    // Google sign-in it is skipped unless the cluster sends codes by SMS
    // only (or the Google email is not on the account). An older API that
    // does not send the flag falls back to the cluster setting.
    const otpRequired =
      typeof res?.body?.data?.otpRequired === "boolean"
        ? res.body.data.otpRequired
        : accountDetails?.data?.two_factor_auth === SettingsValue.YES;

    dispatch(onSetIsLogin(!otpRequired));

    let payload = {
      accounts_id: res?.body?.data?.accountData?.id,
    };
    dispatch(notificationCountGet(payload));
    if (guestLogin) {
      // The token from this login is not a session until the code is entered,
      // so the overlay stays open for the code steps.
      if (otpRequired && onOtpRequired) {
        onOtpRequired();
      } else {
        setShowOverlay?.(false);
      }
    } else {
      if (otpRequired) {
        navigate(`/${cluster_id}/otp-send`);
      } else {
        if (
          accountDetails?.data?.landing_page &&
          accountDetails?.data?.landing_page?.toLowerCase() === "payment"
        ) {
          navigate(`/${cluster_id}/make-payment`);
        } else if (
          accountDetails?.data?.landing_page &&
          accountDetails?.data?.landing_page?.toLowerCase() === "profile"
        ) {
          navigate(`/${cluster_id}/my-profile`);
        } else {
          navigate(`/${cluster_id}/dashboard`);
        }
      }
    }
  };

  const onGoogleCredential = async (credential: string) => {
    onStart?.();
    try {
      const res: any = await dispatch(
        userGoogleLogin({ credential, clusterId: getClusterId() })
      ).unwrap();
      if (res?.body?.success && res?.body?.token) {
        completeLogin(res);
      } else if (
        res?.body?.success &&
        res?.body?.data?.isAttachMultiple === "multiple"
      ) {
        const email = res?.body?.data?.email || "";
        const googleLoginAttemptId =
          res?.body?.data?.googleLoginAttemptId || "";
        if (!googleLoginAttemptId) {
          onNoMatch?.();
          return;
        }
        if (onMultiple) {
          onMultiple({ email, googleLoginAttemptId });
        } else {
          navigate(`/${cluster_id}/login`, {
            state: { googleEmail: email, googleLoginAttemptId },
          });
        }
      } else {
        onNoMatch?.();
      }
    } catch {
      onNoMatch?.();
    } finally {
      onFinish?.();
    }
  };

  const completeGoogleAccountSelection = async ({
    googleLoginAttemptId,
    accountId,
  }: GoogleAccountSelection) => {
    onStart?.();
    try {
      const res: any = await dispatch(
        userGoogleLogin({
          googleLoginAttemptId,
          accountId,
          clusterId: getClusterId(),
        })
      ).unwrap();

      if (res?.body?.success && res?.body?.token) {
        completeLogin(res);
        return true;
      }

      onNoMatch?.();
      return false;
    } catch {
      onNoMatch?.();
      return false;
    } finally {
      onFinish?.();
    }
  };

  return {
    onGoogleCredential,
    completeGoogleAccountSelection,
    completeLogin,
  };
};

export default useGoogleAuth;
